"use client"

import { useAppKit, useAppKitAccount, useAppKitNetwork, useDisconnect as useAppKitDisconnect } from '@reown/appkit/react'
import { useBalance, useAccount, useDisconnect as useWagmiDisconnect } from 'wagmi'
import { useWallet } from '@/lib/wallet-context'
import { Button } from "@/components/ui/button"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { LogOut, Copy, Check, LayoutDashboard, Gift, Settings, ChevronDown, Wallet, ArrowUpRight } from "lucide-react"
import Link from "next/link"
import { useState } from "react"
import { formatEther } from 'viem'
import { motion } from "framer-motion"


// Network icons as inline SVG components - supports both testnet and mainnet chain IDs
const NetworkIcons: Record<string, React.ReactNode> = {
  // BNB Smart Chain (mainnet: 56, testnet: 97)
  '56': (
    <svg className="h-4 w-4" viewBox="0 0 32 32" fill="none">
      <circle cx="16" cy="16" r="16" fill="#F3BA2F" />
      <path d="M16 6L19.5 9.5L13.5 15.5L10 12L16 6Z" fill="white" />
      <path d="M22.5 12.5L26 16L22.5 19.5L19 16L22.5 12.5Z" fill="white" />
      <path d="M9.5 12.5L13 16L9.5 19.5L6 16L9.5 12.5Z" fill="white" />
      <path d="M16 18.5L19.5 22L16 25.5L12.5 22L16 18.5Z" fill="white" />
      <path d="M16 12L19.5 15.5L16 19L12.5 15.5L16 12Z" fill="white" />
    </svg>
  ),
  '97': ( // BSC Testnet
    <svg className="h-4 w-4" viewBox="0 0 32 32" fill="none">
      <circle cx="16" cy="16" r="16" fill="#F3BA2F" />
      <path d="M16 6L19.5 9.5L13.5 15.5L10 12L16 6Z" fill="white" />
      <path d="M22.5 12.5L26 16L22.5 19.5L19 16L22.5 12.5Z" fill="white" />
      <path d="M9.5 12.5L13 16L9.5 19.5L6 16L9.5 12.5Z" fill="white" />
      <path d="M16 18.5L19.5 22L16 25.5L12.5 22L16 18.5Z" fill="white" />
      <path d="M16 12L19.5 15.5L16 19L12.5 15.5L16 12Z" fill="white" />
    </svg>
  ),
  // Ethereum (mainnet: 1, Sepolia: 11155111)
  '1': (
    <svg className="h-4 w-4" viewBox="0 0 32 32" fill="none">
      <circle cx="16" cy="16" r="16" fill="#627EEA" />
      <path d="M16 4V12.87L23 16.22L16 4Z" fill="white" fillOpacity="0.6" />
      <path d="M16 4L9 16.22L16 12.87V4Z" fill="white" />
      <path d="M16 21.97V28L23 17.62L16 21.97Z" fill="white" fillOpacity="0.6" />
      <path d="M16 28V21.97L9 17.62L16 28Z" fill="white" />
      <path d="M16 20.57L23 16.22L16 12.87V20.57Z" fill="white" fillOpacity="0.2" />
      <path d="M9 16.22L16 20.57V12.87L9 16.22Z" fill="white" fillOpacity="0.6" />
    </svg>
  ),
  '11155111': ( // Sepolia
    <svg className="h-4 w-4" viewBox="0 0 32 32" fill="none">
      <circle cx="16" cy="16" r="16" fill="#627EEA" />
      <path d="M16 4V12.87L23 16.22L16 4Z" fill="white" fillOpacity="0.6" />
      <path d="M16 4L9 16.22L16 12.87V4Z" fill="white" />
      <path d="M16 21.97V28L23 17.62L16 21.97Z" fill="white" fillOpacity="0.6" />
      <path d="M16 28V21.97L9 17.62L16 28Z" fill="white" />
      <path d="M16 20.57L23 16.22L16 12.87V20.57Z" fill="white" fillOpacity="0.2" />
      <path d="M9 16.22L16 20.57V12.87L9 16.22Z" fill="white" fillOpacity="0.6" />
    </svg>
  ),
  // Polygon (mainnet: 137, Amoy: 80002)
  '137': (
    <svg className="h-4 w-4" viewBox="0 0 32 32" fill="none">
      <circle cx="16" cy="16" r="16" fill="#8247E5" />
      <path d="M21.5 12.5C21.1 12.3 20.6 12.3 20.2 12.5L17.5 14.1L15.7 15.1L13 16.7C12.6 16.9 12.1 16.9 11.7 16.7L9.6 15.5C9.2 15.3 9 14.9 9 14.4V12.1C9 11.6 9.2 11.2 9.6 11L11.7 9.8C12.1 9.6 12.6 9.6 13 9.8L15.1 11C15.5 11.2 15.7 11.6 15.7 12.1V13.7L17.5 12.6V11C17.5 10.5 17.3 10.1 16.9 9.9L13.1 7.7C12.7 7.5 12.2 7.5 11.8 7.7L7.9 9.9C7.5 10.1 7.3 10.5 7.3 11V15.5C7.3 16 7.5 16.4 7.9 16.6L11.8 18.8C12.2 19 12.7 19 13.1 18.8L15.8 17.3L17.6 16.2L20.3 14.7C20.7 14.5 21.2 14.5 21.6 14.7L23.7 15.9C24.1 16.1 24.3 16.5 24.3 17V19.3C24.3 19.8 24.1 20.2 23.7 20.4L21.6 21.6C21.2 21.8 20.7 21.8 20.3 21.6L18.2 20.4C17.8 20.2 17.6 19.8 17.6 19.3V17.7L15.8 18.8V20.4C15.8 20.9 16 21.3 16.4 21.5L20.3 23.7C20.7 23.9 21.2 23.9 21.6 23.7L25.5 21.5C25.9 21.3 26.1 20.9 26.1 20.4V15.9C26.1 15.4 25.9 15 25.5 14.8L21.5 12.5Z" fill="white" />
    </svg>
  ),
  '80002': ( // Polygon Amoy
    <svg className="h-4 w-4" viewBox="0 0 32 32" fill="none">
      <circle cx="16" cy="16" r="16" fill="#8247E5" />
      <path d="M21.5 12.5C21.1 12.3 20.6 12.3 20.2 12.5L17.5 14.1L15.7 15.1L13 16.7C12.6 16.9 12.1 16.9 11.7 16.7L9.6 15.5C9.2 15.3 9 14.9 9 14.4V12.1C9 11.6 9.2 11.2 9.6 11L11.7 9.8C12.1 9.6 12.6 9.6 13 9.8L15.1 11C15.5 11.2 15.7 11.6 15.7 12.1V13.7L17.5 12.6V11C17.5 10.5 17.3 10.1 16.9 9.9L13.1 7.7C12.7 7.5 12.2 7.5 11.8 7.7L7.9 9.9C7.5 10.1 7.3 10.5 7.3 11V15.5C7.3 16 7.5 16.4 7.9 16.6L11.8 18.8C12.2 19 12.7 19 13.1 18.8L15.8 17.3L17.6 16.2L20.3 14.7C20.7 14.5 21.2 14.5 21.6 14.7L23.7 15.9C24.1 16.1 24.3 16.5 24.3 17V19.3C24.3 19.8 24.1 20.2 23.7 20.4L21.6 21.6C21.2 21.8 20.7 21.8 20.3 21.6L18.2 20.4C17.8 20.2 17.6 19.8 17.6 19.3V17.7L15.8 18.8V20.4C15.8 20.9 16 21.3 16.4 21.5L20.3 23.7C20.7 23.9 21.2 23.9 21.6 23.7L25.5 21.5C25.9 21.3 26.1 20.9 26.1 20.4V15.9C26.1 15.4 25.9 15 25.5 14.8L21.5 12.5Z" fill="white" />
    </svg>
  ),
  // Base (mainnet: 8453, Sepolia: 84532)
  '8453': (
    <svg className="h-4 w-4" viewBox="0 0 32 32" fill="none">
      <circle cx="16" cy="16" r="16" fill="#0052FF" />
      <path d="M16 26C21.5228 26 26 21.5228 26 16C26 10.4772 21.5228 6 16 6C10.4772 6 6 10.4772 6 16C6 21.5228 10.4772 26 16 26Z" fill="#0052FF" />
      <path d="M16 23.5C11.8579 23.5 8.5 20.1421 8.5 16C8.5 11.8579 11.8579 8.5 16 8.5C19.8287 8.5 22.9877 11.3805 23.4524 15.0833H18.0417C17.6494 13.8578 16.5017 13 15.1667 13C13.4178 13 12 14.4178 12 16.1667C12 17.9155 13.4178 19.3333 15.1667 19.3333C16.5017 19.3333 17.6494 18.4755 18.0417 17.25H23.4524C22.9877 20.9528 19.8287 23.5 16 23.5Z" fill="white" />
    </svg>
  ),
  '84532': ( // Base Sepolia
    <svg className="h-4 w-4" viewBox="0 0 32 32" fill="none">
      <circle cx="16" cy="16" r="16" fill="#0052FF" />
      <path d="M16 26C21.5228 26 26 21.5228 26 16C26 10.4772 21.5228 6 16 6C10.4772 6 6 10.4772 6 16C6 21.5228 10.4772 26 16 26Z" fill="#0052FF" />
      <path d="M16 23.5C11.8579 23.5 8.5 20.1421 8.5 16C8.5 11.8579 11.8579 8.5 16 8.5C19.8287 8.5 22.9877 11.3805 23.4524 15.0833H18.0417C17.6494 13.8578 16.5017 13 15.1667 13C13.4178 13 12 14.4178 12 16.1667C12 17.9155 13.4178 19.3333 15.1667 19.3333C16.5017 19.3333 17.6494 18.4755 18.0417 17.25H23.4524C22.9877 20.9528 19.8287 23.5 16 23.5Z" fill="white" />
    </svg>
  ),
  // Tron (Shasta testnet, Nile testnet, Mainnet)
  'tron': (
    <svg className="h-4 w-4" viewBox="0 0 32 32" fill="none">
      <circle cx="16" cy="16" r="16" fill="#EF0027" />
      <path d="M21.932 9.913L7.5 7.257l7.595 19.112 10.583-12.894-3.746-3.562zm-.232 1.17l2.208 2.099-6.038 1.093 3.83-3.192zm-5.142 2.058l-6.678-1.201 8.163 1.454-1.485-.253zm-6.094-.053l6.685 6.182.354 8.04-7.039-14.222zm7.897 6.468l5.473-6.673-5.955.977.482 5.696z" fill="white" />
    </svg>
  ),
  // TON (The Open Network - Mainnet, Testnet)
  'ton': (
    <svg className="h-4 w-4" viewBox="0 0 32 32" fill="none">
      <circle cx="16" cy="16" r="16" fill="#0098EA" />
      <path d="M16 6L8 16H14.5V26L24 16H17.5V6H16Z" fill="white" />
    </svg>
  ),
}

// Tron icon component for reuse
const TronIcon = (
  <svg className="h-4 w-4" viewBox="0 0 32 32" fill="none">
    <circle cx="16" cy="16" r="16" fill="#EF0027" />
    <path d="M21.932 9.913L7.5 7.257l7.595 19.112 10.583-12.894-3.746-3.562zm-.232 1.17l2.208 2.099-6.038 1.093 3.83-3.192zm-5.142 2.058l-6.678-1.201 8.163 1.454-1.485-.253zm-6.094-.053l6.685 6.182.354 8.04-7.039-14.222zm7.897 6.468l5.473-6.673-5.955.977.482 5.696z" fill="white" />
  </svg>
)

// TON icon component for reuse
const TonIcon = (
  <svg className="h-4 w-4" viewBox="0 0 32 32" fill="none">
    <circle cx="16" cy="16" r="16" fill="#0098EA" />
    <path d="M16 6L8 16H14.5V26L24 16H17.5V6H16Z" fill="white" />
  </svg>
)

// Get network icon by chain ID or network name
function getNetworkIcon(chainId: number | string | undefined, networkName?: string): React.ReactNode {
  // Check for Tron or TON networks first (by network name)
  if (networkName) {
    const lowerName = networkName.toLowerCase()
    if (lowerName.includes('tron')) {
      return TronIcon
    }
    if (lowerName.includes('ton')) {
      return TonIcon
    }
  }
  if (!chainId) return null
  const id = String(chainId)
  return NetworkIcons[id] || null
}

// Get currency symbol based on network name
function getCurrencySymbol(networkName: string | undefined, caipCurrency?: string): string {
  if (!networkName) return caipCurrency || 'ETH'
  const lowerName = networkName.toLowerCase()
  if (lowerName.includes('tron')) return 'TRX'
  if (lowerName.includes('ton')) return 'TON'
  if (lowerName.includes('solana')) return 'SOL'
  if (lowerName.includes('bsc') || lowerName.includes('bnb')) return 'BNB'
  if (lowerName.includes('polygon') || lowerName.includes('amoy')) return 'MATIC'
  if (lowerName.includes('base')) return 'ETH'
  return caipCurrency || 'ETH'
}

// Get short network name - supports both testnet and mainnet
function getShortNetworkName(name: string | undefined): string {
  if (!name) return 'Network'
  const shortNames: Record<string, string> = {
    // Mainnets
    'BNB Smart Chain': 'BSC',
    'Ethereum': 'ETH',
    'Polygon': 'Polygon',
    'Base': 'Base',
    // Testnets
    'BSC Testnet': 'BSC-Test',
    'BNB Smart Chain Testnet': 'BSC-Test',
    'Sepolia': 'Sepolia',
    'Polygon Amoy': 'Amoy',
    'Base Sepolia': 'Base-Test',
  }
  return shortNames[name] || name
}

export function WalletButton() {
  const [copied, setCopied] = useState(false)
  const { open } = useAppKit()
  // Use unified wallet context for isConnected and address (supports Tron)
  const { isConnected, address, network, balance: walletContextBalance, disconnectWallet } = useWallet()
  const { connector } = useAccount()
  const { disconnect: wagmiDisconnect } = useWagmiDisconnect()
  const { disconnect: appKitDisconnect } = useAppKitDisconnect()
  const { caipNetwork } = useAppKitNetwork()

  // Universal disconnect that works for both EVM, Solana, and Tron
  const handleDisconnect = () => {
    console.log('[WalletButton] Disconnecting wallet...')
    // Use the unified disconnect from wallet context
    disconnectWallet()
  }

  // wagmi balance as fallback for EVM chains
  const { data: evmBalance } = useBalance({
    address: address as `0x${string}` | undefined,
  })

  const copyAddress = () => {
    if (address) {
      navigator.clipboard.writeText(address)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    }
  }

  const formatAddress = (addr: string) => {
    return `${addr.slice(0, 6)}...${addr.slice(-4)}`
  }

  const getConnectorIcon = () => {
    if (!connector) return <Wallet className="h-5 w-5 text-gray-400" />

    // Some connectors provide icons, others we can map by name
    if (connector.icon) {
      return <img src={connector.icon} alt={connector.name} className="h-5 w-5 rounded-sm" />
    }

    const name = connector.name.toLowerCase()
    if (name.includes('metamask')) {
      return (
        <svg className="h-5 w-5" viewBox="0 0 32 32" fill="none">
          <path d="M30 14.5L25.5 2L19.5 12.5L30 14.5Z" fill="#E17D2E" />
          <path d="M2 14.5L6.5 2L12.5 12.5L2 14.5Z" fill="#E17D2E" />
          <path d="M26.5 24L25.5 29.5L18.5 24H26.5Z" fill="#E17D2E" />
          <path d="M5.5 24L6.5 29.5L13.5 24H5.5Z" fill="#E17D2E" />
          <path d="M11 20L8 16L12.5 13L16 16.5L19.5 13L24 16L21 20H11Z" fill="#E4761B" />
          <path d="M19.5 13L16 16.5V26L23 20H19.5V13Z" fill="#D5BFB2" />
          <path d="M12.5 13L16 16.5V26L9 20H12.5V13Z" fill="#D5BFB2" />
        </svg>
      )
    }
    return <Wallet className="h-5 w-5 text-gray-400" />
  }

  // Use wallet context balance (works for Solana), fall back to EVM balance
  const formattedBalance = walletContextBalance
    ? walletContextBalance
    : evmBalance
      ? parseFloat(formatEther(evmBalance.value)).toFixed(4)
      : '0.0000'

  // Use unified network from context (supports Tron)
  const displayNetwork = network || caipNetwork?.name || 'Unknown'
  // Extract chain ID from CAIP network (only for EVM/Solana)
  const chainId = caipNetwork?.id ? String(caipNetwork.id).split(':').pop() : undefined
  const networkIcon = getNetworkIcon(chainId, displayNetwork)
  // Get currency symbol based on network
  const currencySymbol = getCurrencySymbol(displayNetwork, caipNetwork?.nativeCurrency?.symbol)

  if (!isConnected) {
    return (
      <motion.div
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
      >
        <Button
          onClick={() => open({ view: 'Connect' })}
          variant="stargate"
          size="sm"
          className="h-9 px-4 text-sm gap-2"
        >
          <Wallet className="h-4 w-4" />
          <span className="hidden sm:inline">Connect Wallet</span>
          <span className="sm:hidden">Connect</span>
        </Button>
      </motion.div>
    )
  }

  return (
    <div className="flex items-center gap-2">

      {/* Account Capsule */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <motion.div
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            className="cursor-pointer"
          >
            <div className="flex items-center gap-2.5 dark:bg-[#111111] bg-white dark:hover:bg-[#1a1a1a] hover:bg-gray-50 border dark:border-white/10 border-black/5 rounded-full px-2.5 py-1.5 transition-all duration-200 shadow-lg group h-[38px] min-w-[130px]">
              {/* Wallet Icon Container */}
              <div className="flex items-center justify-center w-7 h-7 group-hover:scale-110 transition-transform duration-200">
                {getConnectorIcon()}
              </div>

              {/* Address and Network Info */}
              <div className="flex flex-col justify-center gap-0">
                <span className="dark:text-white text-gray-900 text-[11px] font-bold leading-none tracking-tight">
                  {address && formatAddress(address)}
                </span>
                <span className="dark:text-white/40 text-gray-500 text-[9px] font-medium leading-none lowercase mt-0.5">
                  {displayNetwork}
                </span>
              </div>

              {/* Status Indicator */}
              <div className="ml-auto h-1.5 w-1.5 rounded-full bg-[#7ed957] shadow-[0_0_8px_rgba(34,197,94,0.6)]" />
            </div>
          </motion.div>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-56">
          <DropdownMenuLabel>My Account</DropdownMenuLabel>
          <DropdownMenuSeparator />
          <div className="px-2 py-2 text-sm">
            <div className="flex items-center justify-between mb-1">
              <span className="text-muted-foreground">Balance:</span>
              <span className="font-medium">{formattedBalance} {currencySymbol}</span>
            </div>
            <div className="flex items-center justify-between mb-1">
              <span className="text-muted-foreground">Network:</span>
              <button
                onClick={() => open({ view: 'Networks' })}
                className="flex items-center gap-1.5 text-xs hover:text-primary transition-colors"
              >
                {networkIcon}
                {displayNetwork}
                <ChevronDown className="h-3 w-3" />
              </button>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">Address:</span>
              <button
                onClick={copyAddress}
                className="flex items-center gap-1 text-xs hover:text-primary transition-colors"
              >
                {address && formatAddress(address)}
                {copied ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
              </button>
            </div>
          </div>
          <DropdownMenuSeparator />
          <DropdownMenuItem asChild>
            <Link href="/dashboard" className="flex items-center gap-2 cursor-pointer">
              <LayoutDashboard className="h-4 w-4" />
              Dashboard
            </Link>
          </DropdownMenuItem>
          <DropdownMenuItem asChild>
            <Link href="/dashboard/referrals" className="flex items-center gap-2 cursor-pointer">
              <Gift className="h-4 w-4" />
              My Referrals
            </Link>
          </DropdownMenuItem>
          <DropdownMenuItem asChild>
            <Link href="/dashboard/settings" className="flex items-center gap-2 cursor-pointer">
              <Settings className="h-4 w-4" />
              Settings
            </Link>
          </DropdownMenuItem>
          <DropdownMenuItem asChild>
            <Link href="/launch-app" className="flex items-center gap-2 cursor-pointer">
              <ArrowUpRight className="h-4 w-4" />
              Launch App
            </Link>
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={handleDisconnect} className="text-destructive focus:text-destructive cursor-pointer">
            <LogOut className="mr-2 h-4 w-4" />
            <span>Disconnect</span>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  )
}
